/* eslint-disable complexity */
/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsService } from '@shared/services/decs/decs.service';
import { map, Observable, of } from 'rxjs';
import { DateTime, Duration } from 'luxon';
import { OutboundConnectionsView } from './models/outbound-connections.view';

@Injectable({
  providedIn: 'root',
})
export class OutboundConnectionsService {
  constructor(
    private decsService: DecsService,
    private alertService: AlertService,
  ) {}

  getOutboundConnections(
    leg: LegView,
    isRefresh = false,
  ): Observable<RequestState<OutboundConnectionsView>> {
    // Only available within 3 days.
    if (!this.isAvailable(leg)) {
      return of(
        new RequestState<OutboundConnectionsView>(
          RequestStatus.IsCompleteWithResponse,
          {
            connections: [],
            totalConnecting: 0,
            totalLocal: 0,
            timestamp: DateTime.now(),
          },
        ),
      );
    }

    const cmd = `G*${leg.flightNumber}/${
      leg.depTimeSched?.toFormat('ddMMM') || ''
    }${leg.depStation}/CPI`;

    return this.decsService.submitResRequest({ cmd }, isRefresh).pipe(
      map((response) => {
        if (response.hasError) {
          this.showErrorAlert();
        }

        if (!response.response) {
          return response.into<OutboundConnectionsView>();
        }

        const sabreText = response.response.result;

        return response.into<OutboundConnectionsView>(
          this.parseConnections(sabreText, response.response.timestamp, leg),
        );
      }),
    );
  }

  // Check if scheduled departure is within 3 calendar days.
  private isAvailable(leg: LegView): boolean {
    if (!leg.depTimeSchedUTC) {
      return false;
    }

    const diff = leg.depTimeSchedUTC.diff(DateTime.now().toUTC()).as('days');

    if (diff < 3) {
      return true;
    }

    return false;
  }

  private parseConnections(
    text: string,
    timestamp: string,
    leg: LegView,
  ): OutboundConnectionsView {
    // Example:
    // FLT-AA  420   ARRIVAL CITY-DFW    GATE-B7    BAG CLAIM-B5
    //
    //             CONNECTING  PASSENGER  INFORMATION
    //
    //           1         2         3         4         5         6
    // 0123456789012345678901234567890123456789012345678901234567890
    //
    //   DESTINATION       FLIGHT     GATE    DPTR TIME     PSGRS
    //
    // ASHEVILLE HNDR NC      4260   B27 /B     730P           1
    // AUSTIN         TX      2707   D20 /      833P           3
    // BALTIMORE WASH MD      2251   A23 /      737P           1
    // CEDR RPDS IOWA IA      3798   B21 /B     911P           1
    // TOTAL LCL PSGRS-88   TOTAL CONN PSGRS-43
    //
    // Columns should be static, so use substrings to extract values.

    const connections: OutboundConnectionsView = {
      connections: [],
      totalLocal: 0,
      totalConnecting: 0,
      timestamp: DateTime.fromISO(timestamp, { zone: 'UTC' }),
    };

    const lines = text.split('\n');
    let destLineFound = false;

    for (const line of lines) {
      const destCity = line.substring(0, 15).trim();

      if (!destLineFound) {
        if (destCity === 'DESTINATION') {
          destLineFound = true;
          continue;
        } else {
          continue;
        }
      } else if (destCity.includes('NO CONNECTING')) {
        continue;
      } else if (destCity.startsWith('TOTAL')) {
        // Extract total values
      } else if (destCity.length === 0) {
        // Blank line, ignore
        continue;
      } else if (line.length >= 57) {
        // Extract column values
        const destState = line.substring(15, 17).trim();
        const flight = line.substring(22, 27).trim();
        const gate = line.substring(30, 34).trim();
        const depTime = line.substring(40, 45).trim();
        const paxCount = parseInt(line.substring(53, 58).trim());

        const arrTime =
          leg.arrTimeActual || leg.arrTimeResched || leg.arrTimeSched;

        let connectionDateTime = this.parseTime(depTime, arrTime);
        let delta = this.getDelta(arrTime, connectionDateTime);

        // If connection time is large negative number, the departure date is likely the next day.
        if (delta && delta.as('minutes') < -240) {
          connectionDateTime = connectionDateTime?.plus({ days: 1 });
          delta = this.getDelta(arrTime, connectionDateTime);
        }

        connections.connections.push({
          destCity,
          destState,
          flight,
          gate,
          depTime: connectionDateTime,
          delta,
          paxCount,
        });
      }
    }

    return connections;
  }

  // Parse string time like 921P into DateTime object.
  private parseTime(
    time: string,
    arrTime: DateTime | null,
  ): DateTime | undefined {
    if (time === '') {
      return undefined;
    }
    const timeNum = time.slice(0, -1);
    let hour = parseInt(timeNum.slice(0, -2));
    const minute = parseInt(timeNum.slice(-2));
    const timeAmPm = time.slice(-1);

    if (timeAmPm === 'P' && hour < 12) {
      hour += 12;
    }

    if (timeAmPm === 'A' && hour === 12) {
      hour = 0;
    }

    const connectionTime = DateTime.fromObject(
      {
        year: arrTime?.year,
        month: arrTime?.month,
        day: arrTime?.day,
        hour,
        minute,
      },
      { zone: 'UTC' },
    );

    return connectionTime;
  }

  // Calculate difference between arrival time and connection departure time.
  // Modify connection departure time under certain circumstances.
  private getDelta(
    arrTime?: DateTime | null,
    connTime?: DateTime,
  ): Duration | undefined {
    if (!arrTime || !connTime) {
      return undefined;
    }

    return connTime.diff(arrTime);
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get passenger outbound connection information.',
    });
  }
}
